<!-- Ticket Header Container -->
<div class="ticket-header">
  <!-- Left Section: Title, Status, Module Info -->
  <div class="ticket-header__left">
    <!-- Title & Status Badge -->
    <div class="ticket-title-section">
      <h1 class="ticket-title">{{ getDisplayTitle() }}</h1>

      <!-- Status Badge (if status exists) -->
      @if (status) {
        <app-status-pill
          variant="solid"
          [color]="selectedStatus?.color"
          [label]="getFormattedStatus()"
          [locked]="getSelectedStatusLocked()"
          [truncateAt]="30">
        </app-status-pill>
      }
    </div>

    <!-- Module Info (Module • Reference • Milestone) -->
    @if (projectName || milestone) {
      <div class="ticket-info-breadcrumb">
        <!-- Module & Reference: linked record, when the module is one the
             ticket's linked record can route to (see getRecordRoute()) -->
        @if (projectName) {
          <span class="info-label">{{ moduleLabel }}:</span>
          @if (recordRoute) {
            <a class="info-value record-link" [routerLink]="recordRoute" (click)="recordLinkClick.emit()">{{ projectName }}</a>
            <span class="separator">•</span>
            <a class="project-id record-link" [routerLink]="recordRoute" (click)="recordLinkClick.emit()">#{{ projectId }}</a>
          } @else {
            <span class="info-value">{{ projectName }}</span>
          }
        }
        <!-- Milestone (if attached) -->
        @if (milestone) {
          <span class="breadcrumb-separator">•</span>
          <div class="milestone-indicator">
            <mat-icon class="milestone-icon">flag</mat-icon>
            <span class="milestone-name">{{ milestone.name }}</span>
          </div>
        }
      </div>
    }
  </div>

  <!-- Right Section: Action Buttons -->
  <div class="ticket-header__right">

    <!-- Billable Toggle Button -->
    <button
      mat-icon-button
      class="action-button billable-toggle-button"
      [class.billable-active]="isBillable"
      (click)="onBillableToggle()"
      [disabled]="mode === 'view' || isLocked"
      [ngClass]="{'permission-disabled': isLocked}"
      [attr.aria-label]="mode === 'view' ? 'Billable status (read only)' : (isBillable ? 'Remove billable' : 'Set billable')"
      [matTooltip]="mode === 'view' ? 'Billable status managed in edit mode' : (isBillable ? 'Billable (Click to remove)' : 'Set Billable')">
      <mat-icon>payment</mat-icon>
    </button>

    <!-- Priority Toggle Button -->
    <button
      mat-icon-button
      class="action-button priority-toggle-button"
      [class.priority-active]="isHighPriority || priority === 3 || priority === 4"
      (click)="onPriorityToggle()"
      [disabled]="isLocked"
      [ngClass]="{'permission-disabled': isLocked}"
      [attr.aria-label]="isHighPriority || priority === 3 || priority === 4 ? 'Remove high priority' : 'Set high priority'"
      [matTooltip]="isHighPriority || priority === 3 || priority === 4 ? 'High Priority (Click to remove)' : 'Set High Priority'">
      <mat-icon>local_fire_department</mat-icon>
    </button>

    <!-- Lock/Unlock Button (View Mode Only) -->
    @if (mode === 'view') {
      <button
        mat-icon-button
        class="action-button lock-toggle-button"
        [class.locked]="isLocked"
        (click)="onLockToggle()"
        appPermission="TICKET-LOCKUNLOCK"
        [attr.aria-label]="isLocked ? 'Unlock ticket' : 'Lock ticket'"
        [matTooltip]="isLocked ? 'Ticket Locked - Click to Unlock' : 'Lock Ticket'">
        <mat-icon>{{ isLocked ? 'lock' : 'lock_open' }}</mat-icon>
      </button>
    }

    <!-- Mark Complete Button (View Mode Only) -->
    @if (mode === 'view' && showMarkComplete) {
      <button
        mat-icon-button
        class="action-button action-button--success"
        [appPermission]="markCompletePermission"
        [disabled]="isLocked"
        [ngClass]="{'permission-disabled': isLocked}"
        (click)="onMarkComplete()"
        [attr.aria-label]="'Mark complete'"
        matTooltip="Mark Complete">
        <mat-icon>check_circle</mat-icon>
      </button>
    }

    <!-- PDF Export Button (View Mode Only) -->
    @if (mode === 'view' && showPdfExport) {
      <button
        mat-icon-button
        class="action-button action-button--info"
        [appPermission]="pdfExportPermission"
        (click)="onExportPDF()"
        [attr.aria-label]="'Export PDF'"
        matTooltip="Export PDF">
        <mat-icon>description</mat-icon>
      </button>
    }

    <!-- Copy Button (View Mode Only) -->
    @if (mode === 'view' && showCopy) {
      <button
        mat-icon-button
        class="action-button action-button--info"
        [appPermission]="copyPermission"
        [disabled]="isLocked"
        [ngClass]="{'permission-disabled': isLocked}"
        (click)="onCopy()"
        [attr.aria-label]="'Copy ticket'"
        matTooltip="Copy Ticket">
        <mat-icon>content_copy</mat-icon>
      </button>
    }

    <!-- Edit Button (View Mode Only) -->
    @if (mode === 'view' && showEdit) {
      <button
        mat-raised-button
        class="save-button"
        [appPermission]="editPermission"
        [disabled]="isLocked"
        [ngClass]="{'permission-disabled': isLocked}"
        (click)="onEdit()"
        [attr.aria-label]="'Edit ticket'"
        matTooltip="Edit ticket">
        <mat-icon>edit_note</mat-icon>
        <span class="button-text">Edit</span>
      </button>
    }

    <!-- Action Group 4.25: New Ticket Button (View Mode Only) -->
    @if (mode === 'view' && showNewTicket) {
      <div class="action-group">
        <button type="button"
          mat-button
          class="header-btn header-btn--secondary"
          [appPermission]="newTicketPermission"
          [disabled]="isLocked"
          [ngClass]="{'permission-disabled': isLocked}"
          (click)="onNewTicket()"
          [attr.aria-label]="'Create new ticket'">
          <mat-icon>add</mat-icon>
          <span class="button-text">New Ticket</span>
        </button>
      </div>
    }

    <!-- Search Toggle Button -->
    <!-- <button
    mat-icon-button
    class="action-button"
    [class.active]="showSearch"
    (click)="toggleSearch()"
    [attr.aria-label]="showSearch ? 'Close search' : 'Search ticket'"
    [matTooltip]="showSearch ? 'Close search' : 'Search'">
    <mat-icon>{{ showSearch ? 'search_off' : 'search' }}</mat-icon>
  </button> -->

  <!-- Fullscreen Toggle Button -->
  <!-- <button
  mat-icon-button
  class="action-button"
  (click)="onFullscreenToggle()"
  [attr.aria-label]="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
  [matTooltip]="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'">
  <mat-icon>{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</mat-icon>
</button> -->

<!-- Delete Button (Edit Mode) -->
@if (mode === 'edit' && showDelete) {
  <button
    mat-icon-button
    class="action-button action-button--danger"
    (click)="onDelete()"
    [attr.aria-label]="'Delete ticket'"
    matTooltip="Delete ticket">
    <mat-icon>delete_forever</mat-icon>
  </button>
}

<!-- Close Button -->
<button
  mat-icon-button
  class="action-button"
  (click)="onClose()"
  [attr.aria-label]="'Close'"
  matTooltip="Close">
  <mat-icon>close</mat-icon>
</button>
</div>
</div>

<!-- Animated Search Bar (appears below header) -->
@if (showSearch) {
  <div class="search-bar-container" [@slideDown]>
    <div class="search-input-wrapper">
      <mat-icon class="search-icon">search</mat-icon>
      <input #searchInput
        type="text"
        class="search-input"
        [value]="searchQuery"
        (input)="onSearchChange($event)"
        placeholder="Search within this ticket..."
        [attr.aria-label]="'Search within ticket'">
      <button type="button"
        class="search-close-btn"
        (click)="toggleSearch()"
        [attr.aria-label]="'Close search'">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>
}
